import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  Pencil,
  Power,
  Save,
  X,
  Plus,
  Check,
  AlertTriangle,
  Building2,
  ShieldCheck,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { StatusBadge } from '../../../components/ui/status-badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import {
  type AdminUser,
  type AdminOrganization,
  type AdminRole,
  adminOpsKeys,
  useAddOrgMembership,
  useToggleMembershipActive,
  useUpdateMemberNumber,
  useSetOrgRole,
  useSetUserSystemRole,
} from '../../../api/extensions/useAdminOps';
import {
  useApproveMembershipClaim,
  useRejectMembershipClaim,
} from '../../../api/extensions/useMembershipVerificationOps';
import { useToast } from '../../../contexts/ToastContext';
import DrawPriorityField from './DrawPriorityField';

const NO_ROLE = '__none__';
// ALL-org sentinel — a role attached here is system-wide (= super admin).
const ALL_ORG_ID = '00000000-0000-0000-0000-000000000001';

interface MembershipsTabProps {
  user: AdminUser;
  isAllOrgAdmin: boolean;
  managedOrgIds?: string[];
  availableRoles: AdminRole[];
  selectableOrgs: AdminOrganization[];
  /** Logged-in admin's user_profiles.id — used to block the super admin
      control on the viewer's own profile so a super admin can't accidentally
      revoke themselves. */
  currentUserProfileId?: string;
}

export default function MembershipsTab({
  user,
  isAllOrgAdmin,
  managedOrgIds,
  availableRoles,
  selectableOrgs,
  currentUserProfileId,
}: MembershipsTabProps) {
  const visibleMemberships = useMemo(() => {
    const list = managedOrgIds
      ? user.organization_memberships.filter((m) =>
          managedOrgIds.includes(m.organization_id),
        )
      : user.organization_memberships;
    return [...list].sort((a, b) => {
      // Pending first — they need an admin's attention.
      const aPending = isPending(a.membership_status);
      const bPending = isPending(b.membership_status);
      if (aPending !== bPending) return aPending ? -1 : 1;
      // Denied next: the contestant can't resubmit, so the only way these
      // ever resolve is an admin approving (or correcting) them here.
      const aDenied = isDenied(a.membership_status);
      const bDenied = isDenied(b.membership_status);
      if (aDenied !== bDenied) return aDenied ? -1 : 1;
      // Active before inactive so the bulk of the list is the live state.
      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
      return (a.organization.short_name || a.organization.name).localeCompare(
        b.organization.short_name || b.organization.name,
      );
    });
  }, [user.organization_memberships, managedOrgIds]);

  const addableOrgs = useMemo(() => {
    const memberOrgIds = new Set(user.organization_memberships.map((m) => m.organization_id));
    return selectableOrgs
      .filter((o) => !memberOrgIds.has(o.id))
      .sort((a, b) =>
        (a.short_name || a.name).localeCompare(b.short_name || b.name),
      );
  }, [selectableOrgs, user.organization_memberships]);

  const pendingCount = visibleMemberships.filter((m) =>
    isPending(m.membership_status),
  ).length;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="space-y-4">
        {/* Super-admin grant/revoke — only visible to ALL-org admins. Lives
            here (above the per-org memberships) since the ALL org is itself
            the "system" scope and we don't surface it as a per-org row. */}
        {isAllOrgAdmin && (
          <SystemAccessSection
            user={user}
            availableRoles={availableRoles}
            currentUserProfileId={currentUserProfileId}
          />
        )}

        {pendingCount > 0 && (
          <div className="rounded-lg border-2 border-amber-300 bg-amber-50 dark:bg-amber-500/5 dark:border-amber-900/60 p-3 flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <div className="text-sm leading-tight">
              <span className="font-medium text-amber-900 dark:text-amber-200">
                {pendingCount} membership claim{pendingCount === 1 ? '' : 's'} awaiting review.
              </span>{' '}
              <span className="text-amber-800 dark:text-amber-300/80">
                Approve or reject inline below — rejecting reverts the user to non-member.
              </span>
            </div>
          </div>
        )}

        <div className="rounded-lg border bg-card text-card-foreground shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b bg-muted/30">
            <h2 className="text-sm font-semibold">
              Organization memberships
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                {visibleMemberships.length} total
              </span>
            </h2>
          </div>

          {visibleMemberships.length === 0 ? (
            <div className="p-12 text-center">
              <Building2 className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">
                No organization memberships yet.
              </p>
              {isAllOrgAdmin && addableOrgs.length > 0 && (
                <p className="text-xs text-muted-foreground/70 mt-1">
                  Use <span className="font-medium">Add to organization</span> below.
                </p>
              )}
            </div>
          ) : (
            // Fixed-height scroll container so a user with 30 memberships
            // stays inside the viewport. Pinned headers + dense rows mean
            // an admin can scan a whole org list at a glance.
            <div className="max-h-[480px] overflow-y-auto divide-y">
              {visibleMemberships.map((m) => (
                <MembershipRow
                  key={m.organization_id}
                  user={user}
                  membership={m}
                  orgRoles={user.roles.filter(
                    (r) => r.organization_id === m.organization_id,
                  )}
                  availableRoles={availableRoles}
                />
              ))}
            </div>
          )}
        </div>

        {isAllOrgAdmin && addableOrgs.length > 0 && (
          <AddToOrgPanel
            userId={user.id}
            orgs={addableOrgs}
            availableRoles={availableRoles}
          />
        )}
      </div>
    </TooltipProvider>
  );
}

/**
 * Single dense row representing one membership. Stays on one line on
 * desktop (wraps gracefully on mobile). Pending claims swap their
 * controls for inline Approve / Reject buttons + an expandable reason.
 */
function MembershipRow({
  user,
  membership,
  orgRoles,
  availableRoles,
}: {
  user: AdminUser;
  membership: AdminUser['organization_memberships'][number];
  orgRoles: AdminUser['roles'];
  availableRoles: AdminRole[];
}) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const orgLabel = membership.organization.short_name || membership.organization.name;
  const pending = isPending(membership.membership_status);
  const denied = isDenied(membership.membership_status);
  const inactive = !membership.is_active;

  // ── Mutations ──
  const toggleActive = useToggleMembershipActive();
  const setOrgRole = useSetOrgRole();
  const updateMemberNumber = useUpdateMemberNumber();
  const approveClaim = useApproveMembershipClaim();
  const rejectClaim = useRejectMembershipClaim();

  // ── Member number inline edit ──
  const [editingNumber, setEditingNumber] = useState(false);
  const [memberNumberDraft, setMemberNumberDraft] = useState(membership.member_number ?? '');

  const handleSaveMemberNumber = async () => {
    const next = memberNumberDraft.trim();
    if ((next || null) === (membership.member_number ?? null)) {
      setEditingNumber(false);
      return;
    }
    try {
      await updateMemberNumber.mutateAsync({
        userId: user.id,
        orgId: membership.organization_id,
        memberNumber: next.length > 0 ? next : null,
      });
      showSuccess('Member number updated');
      setEditingNumber(false);
    } catch (e: any) {
      showError('Failed to update member number', e?.message ?? 'Unknown error');
    }
  };

  // ── Role swap ──
  // Single-role-per-org: the backend PUT endpoint replaces (or clears) the
  // user's role for this org atomically, so the UI no longer has to chain
  // remove+add calls.
  const currentRoleId = orgRoles.length > 0 ? orgRoles[0].role_id : '';

  const handleRoleChange = async (newRoleId: string) => {
    const target = newRoleId === NO_ROLE ? null : newRoleId;
    if ((target ?? '') === currentRoleId) return;
    try {
      await setOrgRole.mutateAsync({
        userId: user.id,
        orgId: membership.organization_id,
        roleId: target,
      });
      const newRoleName = target
        ? (availableRoles.find((r) => r.id === target)?.role_name ?? 'role')
        : 'no role';
      showSuccess(`Role updated to ${newRoleName}`);
    } catch (e: any) {
      showError('Failed to change role', e?.message ?? 'Unknown error');
    }
  };

  const handleToggleActive = () => {
    toggleActive.mutate(
      { userId: user.id, orgId: membership.organization_id },
      {
        onSuccess: () =>
          showSuccess(`${orgLabel} ${membership.is_active ? 'deactivated' : 'activated'}`),
        onError: (e: any) => showError('Failed to toggle', e?.message ?? 'Unknown error'),
      },
    );
  };

  // ── Pending approval ──
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  // Re-approving a DENIED claim reverses a deliberate decision and moves real
  // money (the surcharge backfilled at denial is refunded), so that path gets
  // a confirm step. A pending claim stays one click.
  const [approveMode, setApproveMode] = useState(false);

  const invalidateUser = () => {
    queryClient.invalidateQueries({ queryKey: adminOpsKeys.user(user.id) });
  };

  const handleApprove = async () => {
    try {
      const result = await approveClaim.mutateAsync({
        organizationId: membership.organization_id,
        userProfileId: user.id,
      });
      invalidateUser();
      setApproveMode(false);
      showSuccess(
        `${orgLabel} membership approved`,
        result?.previously_rejected
          ? 'The earlier denial was reversed — member pricing applies again.'
          : undefined,
      );
    } catch (e: any) {
      showError('Failed to approve', e?.message ?? 'Unknown error');
    }
  };

  const handleReject = async () => {
    try {
      await rejectClaim.mutateAsync({
        organizationId: membership.organization_id,
        userProfileId: user.id,
        reason: rejectReason.trim() || undefined,
      });
      invalidateUser();
      showSuccess(`${orgLabel} membership rejected`);
      setRejectMode(false);
      setRejectReason('');
    } catch (e: any) {
      showError('Failed to reject', e?.message ?? 'Unknown error');
    }
  };

  return (
    <div
      className={`px-4 py-2.5 ${
        pending
          ? 'bg-amber-50 dark:bg-amber-500/5'
          : denied
            ? // Denied rows keep full contrast (not the faded `inactive`
              // treatment) — they still need an admin decision.
              'bg-red-50/60 dark:bg-red-500/5'
            : inactive
              ? 'opacity-60 hover:opacity-100 hover:bg-accent/40'
              : 'hover:bg-accent/40'
      } transition-colors`}
    >
      <div className="flex items-center gap-3 flex-wrap">
        {/* Status dot — color-coded so admins can scan state visually */}
        <StatusDot pending={pending} active={membership.is_active} status={membership.membership_status} />

        {/* Org name + full name underneath, truncated to keep the row tight */}
        <div className="min-w-0 flex-1 leading-tight">
          <div className="text-sm font-medium truncate">{orgLabel}</div>
          <div className="text-xs text-muted-foreground truncate">
            {membership.organization.name}
            {!pending && (
              <>
                {' • '}
                <StatusText status={membership.membership_status} active={membership.is_active} />
              </>
            )}
          </div>
        </div>

        {pending ? (
          <>
            <span className="text-xs text-amber-800 dark:text-amber-300/80 hidden sm:inline">
              Member #{membership.member_number ? ` ${membership.member_number}` : ' (none provided)'}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setRejectMode((v) => !v)}
              disabled={approveClaim.isPending || rejectClaim.isPending}
            >
              <X className="h-3.5 w-3.5 mr-1" /> Reject
            </Button>
            <Button
              size="sm"
              onClick={handleApprove}
              disabled={approveClaim.isPending || rejectClaim.isPending}
            >
              {approveClaim.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5 mr-1" />
              )}
              Approve
            </Button>
          </>
        ) : (
          <>
            {/* Role dropdown — compact width to keep the row dense */}
            <div className="flex items-center gap-1 shrink-0">
              <Select
                value={currentRoleId || NO_ROLE}
                onValueChange={handleRoleChange}
                disabled={setOrgRole.isPending}
              >
                <SelectTrigger className="h-8 w-[140px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_ROLE}>— No role —</SelectItem>
                  {availableRoles.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.role_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {setOrgRole.isPending && (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
              )}
            </div>

            {/* Member number — inline edit. Click pencil to swap to input. */}
            <div className="flex items-center gap-1 min-w-[150px]">
              {editingNumber ? (
                <>
                  <Input
                    autoFocus
                    value={memberNumberDraft}
                    onChange={(e) => setMemberNumberDraft(e.target.value)}
                    placeholder="#"
                    maxLength={50}
                    className="h-8 w-[110px] text-xs"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveMemberNumber();
                      if (e.key === 'Escape') {
                        setMemberNumberDraft(membership.member_number ?? '');
                        setEditingNumber(false);
                      }
                    }}
                  />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={handleSaveMemberNumber}
                        disabled={updateMemberNumber.isPending}
                      >
                        {updateMemberNumber.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Save className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Save</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => {
                          setMemberNumberDraft(membership.member_number ?? '');
                          setEditingNumber(false);
                        }}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Cancel</TooltipContent>
                  </Tooltip>
                </>
              ) : (
                <>
                  <span className="text-xs text-muted-foreground">#</span>
                  <span className="font-mono text-xs">
                    {membership.member_number || (
                      <span className="text-muted-foreground/50 font-sans italic">none</span>
                    )}
                  </span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => {
                          setMemberNumberDraft(membership.member_number ?? '');
                          setEditingNumber(true);
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Edit member number</TooltipContent>
                  </Tooltip>
                </>
              )}
            </div>

            {/* Admin-only draw score for this org's ferris wheel. Lazy —
                only fetched when expanded — and never shown to contestants. */}
            <DrawPriorityField
              associationId={membership.organization_id}
              userProfileId={user.id}
            />

            {/* Actions — memberships can be deactivated but never deleted,
                so historical entries / payments stay linked to the org. */}
            <div className="flex items-center gap-1 shrink-0">
              {/* A denied claim is a dead end for the contestant — they can't
                  submit another number — so staff need a way back. Fix the
                  number above if it was wrong, then approve. */}
              {denied && (
                <Button
                  size="sm"
                  onClick={() => setApproveMode((v) => !v)}
                  disabled={approveClaim.isPending}
                >
                  <Check className="h-3.5 w-3.5 mr-1" /> Approve
                </Button>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    disabled={toggleActive.isPending}
                    onClick={handleToggleActive}
                  >
                    <Power
                      className={`h-4 w-4 ${
                        membership.is_active ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'
                      }`}
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {membership.is_active ? 'Deactivate membership' : 'Activate membership'}
                </TooltipContent>
              </Tooltip>
            </div>
          </>
        )}
      </div>

      {/* Multi-role warning fits naturally on its own row when rare */}
      {!pending && orgRoles.length > 1 && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 ml-6">
          User has {orgRoles.length} roles in this org — changing the dropdown will replace them with one.
        </p>
      )}

      {/* Approve-a-denied-claim confirmation — spells out the money movement
          before the admin reverses their own earlier denial. */}
      {denied && approveMode && (
        <div className="mt-2 ml-6 space-y-2 rounded-md border border-emerald-300 bg-emerald-50/60 dark:bg-emerald-500/5 dark:border-emerald-900/60 p-3">
          <p className="text-xs text-muted-foreground">
            Approving reverses the earlier denial for {orgLabel}. Member #
            {membership.member_number || ' (none on file)'} is locked in, the
            non-member fee added at denial is removed from their upcoming
            entries (already-paid surcharges are refunded), and the contestant
            is notified. The denial stays in the membership history.
          </p>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => setApproveMode(false)}>
              Cancel
            </Button>
            <Button size="sm" disabled={approveClaim.isPending} onClick={handleApprove}>
              {approveClaim.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5 mr-1.5" />
              )}
              Confirm approve
            </Button>
          </div>
        </div>
      )}

      {/* Reject reason expander — only renders when rejecting */}
      {pending && rejectMode && (
        <div className="mt-2 ml-6 space-y-2 rounded-md border border-amber-300 bg-amber-50/60 dark:bg-amber-500/5 dark:border-amber-900/60 p-3">
          <Label htmlFor={`reject-${membership.organization_id}`} className="text-xs">
            Reason (sent to the contestant; optional)
          </Label>
          <Textarea
            id={`reject-${membership.organization_id}`}
            rows={2}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. We can't find that member number on file."
          />
          <p className="text-xs text-muted-foreground">
            Rejecting reverts the user to non-member and backfills the non-member fee onto every active
            registration in {orgLabel}.
          </p>
          <div className="flex gap-2 justify-end">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setRejectMode(false);
                setRejectReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={rejectClaim.isPending}
              onClick={handleReject}
            >
              {rejectClaim.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <X className="h-3.5 w-3.5 mr-1.5" />
              )}
              Confirm reject
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Grants or revokes super-admin (ALL-org admin) for the user being viewed.
 * Only rendered when the current viewer is themselves an ALL-org admin —
 * that gate is enforced by the caller. We also block targeting the viewer's
 * own profile so a super admin can't lock themselves out, and only render
 * once both the `admin` and `user` role IDs are known (we need both to
 * grant or revoke — revoking sets the system role back to `user`).
 */
function SystemAccessSection({
  user,
  availableRoles,
  currentUserProfileId,
}: {
  user: AdminUser;
  availableRoles: AdminRole[];
  currentUserProfileId?: string;
}) {
  const { showSuccess, showError } = useToast();
  const setSystemRole = useSetUserSystemRole();

  const isTargetSuperAdmin = user.roles.some(
    (r) => r.organization_id === ALL_ORG_ID && r.role.role_name === 'admin',
  );
  const adminRoleId = availableRoles.find((r) => r.role_name === 'admin')?.id;
  const userRoleId = availableRoles.find((r) => r.role_name === 'user')?.id;
  const isSelf = !!currentUserProfileId && currentUserProfileId === user.id;

  // Bail out if we can't determine both target roles yet — better to hide
  // the control than render a button that would fail on click.
  if (!adminRoleId || !userRoleId) return null;

  const displayName =
    [user.display_first_name, user.display_last_name].filter(Boolean).join(' ') ||
    [user.first_name, user.last_name].filter(Boolean).join(' ') ||
    user.primary_email ||
    'this user';

  const handleToggle = async () => {
    if (isSelf) return;
    const targetRoleId = isTargetSuperAdmin ? userRoleId : adminRoleId;
    try {
      await setSystemRole.mutateAsync({ userId: user.id, roleId: targetRoleId });
      showSuccess(
        isTargetSuperAdmin
          ? `${displayName} is no longer a super admin`
          : `${displayName} is now a super admin`,
        'They must sign out and back in for the change to take effect.',
      );
    } catch (e: any) {
      showError('Failed to update super admin status', e?.message ?? 'Unknown error');
    }
  };

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm overflow-hidden">
      <div className="flex items-start justify-between gap-3 px-4 py-3">
        <div className="flex items-start gap-3 min-w-0">
          <ShieldCheck
            className={`h-5 w-5 mt-0.5 shrink-0 ${
              isTargetSuperAdmin
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-muted-foreground'
            }`}
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-sm font-semibold">System access</h2>
              {isTargetSuperAdmin && (
                <StatusBadge status="admin" variant="role" className="text-xs" />
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {isTargetSuperAdmin
                ? 'Super admin — full access across every organization. They must re-login after a change.'
                : 'Grant system-wide admin access across every organization. They must re-login after a change.'}
            </p>
          </div>
        </div>
        {isSelf ? (
          <span className="text-xs italic text-muted-foreground whitespace-nowrap pt-1">
            you — ask another super admin
          </span>
        ) : (
          <Button
            variant={isTargetSuperAdmin ? 'outline' : 'default'}
            size="sm"
            onClick={handleToggle}
            disabled={setSystemRole.isPending}
            className="whitespace-nowrap shrink-0"
          >
            {setSystemRole.isPending ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : null}
            {isTargetSuperAdmin ? 'Revoke Super Admin' : 'Make Super Admin'}
          </Button>
        )}
      </div>
    </div>
  );
}

/** ALL-org-admin-only "add user to a new org" panel. */
function AddToOrgPanel({
  userId,
  orgs,
  availableRoles,
}: {
  userId: string;
  orgs: AdminOrganization[];
  availableRoles: AdminRole[];
}) {
  const { showSuccess, showError } = useToast();
  const addOrg = useAddOrgMembership();
  // After adding a membership the backend auto-seeds the 'member' role.
  // If the admin picked something else here we route through setOrgRole
  // (the same single-role-per-org endpoint the row dropdown uses) to swap
  // it — calling addRole would now hit the unique index.
  const setOrgRole = useSetOrgRole();

  const [orgId, setOrgId] = useState('');
  const [roleId, setRoleId] = useState(NO_ROLE);
  const [memberNumber, setMemberNumber] = useState('');

  const valid = !!orgId;

  const handleAdd = async () => {
    if (!valid) return;
    const orgLabel = orgs.find((o) => o.id === orgId);
    try {
      await addOrg.mutateAsync({
        userId,
        organizationId: orgId,
        memberNumber: memberNumber.trim() || undefined,
      });
      if (roleId !== NO_ROLE) {
        try {
          await setOrgRole.mutateAsync({
            userId,
            orgId,
            roleId,
          });
        } catch (roleErr: any) {
          showError(
            'Membership added, but role assignment failed',
            roleErr?.message ?? 'You can pick a role above.',
          );
        }
      }
      showSuccess(`Added to ${orgLabel?.short_name || orgLabel?.name || 'organization'}`);
      setOrgId('');
      setRoleId(NO_ROLE);
      setMemberNumber('');
    } catch (e: any) {
      showError('Failed to add to org', e?.message ?? 'Unknown error');
    }
  };

  return (
    <div className="rounded-lg border-2 border-dashed bg-card p-3">
      <div className="flex items-center gap-2 mb-2.5">
        <Plus className="h-4 w-4 text-muted-foreground" />
        <h3 className="font-medium text-sm">Add to organization</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_140px_auto] gap-2 items-end">
        <div>
          <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Organization
          </Label>
          <Select value={orgId} onValueChange={setOrgId}>
            <SelectTrigger className="h-9 mt-1">
              <SelectValue placeholder="Pick an org…" />
            </SelectTrigger>
            <SelectContent>
              {orgs.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.short_name || o.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Initial role
          </Label>
          <Select value={roleId} onValueChange={setRoleId}>
            <SelectTrigger className="h-9 mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_ROLE}>— No role —</SelectItem>
              {availableRoles.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {r.role_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Member #
          </Label>
          <Input
            value={memberNumber}
            onChange={(e) => setMemberNumber(e.target.value)}
            placeholder="—"
            maxLength={50}
            className="h-9 mt-1"
          />
        </div>
        <Button onClick={handleAdd} disabled={!valid || addOrg.isPending} className="h-9">
          {addOrg.isPending ? (
            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
          ) : (
            <Plus className="h-4 w-4 mr-1.5" />
          )}
          Add
        </Button>
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function isPending(status?: string | null): boolean {
  return status === 'pending' || status === 'waiting_for_approval';
}

/**
 * A claim an admin/delegate turned down. The contestant is deliberately
 * blocked from submitting a replacement number themselves, so the denied row
 * sits here until staff either correct the number or approve it after all —
 * both of which this tab has to offer.
 */
function isDenied(status?: string | null): boolean {
  return status === 'denied' || status === 'rejected';
}

function StatusDot({
  pending,
  active,
  status,
}: {
  pending: boolean;
  active: boolean;
  status: string | null | undefined;
}) {
  let cls = 'bg-emerald-500';
  let label = 'Active';
  if (pending) {
    cls = 'bg-amber-500 animate-pulse';
    label = 'Pending approval';
  } else if (status === 'denied' || status === 'rejected') {
    cls = 'bg-red-500';
    label = 'Denied';
  } else if (!active) {
    cls = 'bg-muted-foreground/40';
    label = 'Inactive';
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          aria-label={label}
          className={`inline-block h-2.5 w-2.5 rounded-full shrink-0 ${cls}`}
        />
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

function StatusText({
  status,
  active,
}: {
  status: string | null | undefined;
  active: boolean;
}) {
  if (status === 'denied' || status === 'rejected') {
    return <span className="text-red-700 dark:text-red-400">Denied</span>;
  }
  if (!active) return <span>Inactive</span>;
  if (status === 'approved') return <span>Active</span>;
  return <span>{status ?? 'Active'}</span>;
}
